import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeGet } from '../utils/helpers';
import { supabase, isSupabaseConfigured, testDatabaseConnection } from '@shared/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import { toast } from '@shared/components/ui/Toaster';
import { sleep, retryOperation } from '../utils/helpers';
import { createUserProfile, checkIfProfilesTableExists } from '../utils/profileHelpers';
import PasswordChangePrompt from '../components/PasswordChangePrompt';

// Define the auth context interface
interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  repairUserProfile: (email: string, password: string) => Promise<boolean>;
  loading: boolean;
  isAdmin: boolean;
  refreshAdminStatus: () => Promise<boolean>;
  isConfigured: boolean;
  authInitialized: boolean;
  checkDatabaseConnection: () => Promise<boolean>;
  needsPasswordChange: boolean;
  checkPasswordChangeRequired: () => Promise<boolean>;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  repairUserProfile: async () => false,
  loading: true,
  isAdmin: false,
  refreshAdminStatus: async () => false,
  isConfigured: false,
  authInitialized: false,
  checkDatabaseConnection: async () => false,
  needsPasswordChange: false,
  checkPasswordChangeRequired: async () => false,
});


// Hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isConfigured] = useState(isSupabaseConfigured());
  const [authInitialized, setAuthInitialized] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  
  // Add ref to prevent multiple initializations
  const isInitializingRef = useRef(false);
  const signOutDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastSignOutAttemptRef = useRef<number>(0);
  const adminCheckCacheRef = useRef<{status: boolean; timestamp: number; userId: string} | null>(null);

  // Admin cache duration: 5 minutes
  const ADMIN_CACHE_DURATION = 5 * 60 * 1000;

  // Check if user needs to change password
  const checkPasswordChangeRequired = useCallback(async (currentUser: User | null): Promise<boolean> => {
    if (!currentUser || !isConfigured) return false;

    try {
      const { data: result, error } = await supabase.rpc('needs_password_change', {
        user_id: currentUser.id
      });

      if (error) {
        console.warn('Error checking password change requirement:', error);
        return false;
      }

      const passwordChangeRequired = result === true;
      setNeedsPasswordChange(passwordChangeRequired);
      
      if (passwordChangeRequired) {
        setShowPasswordPrompt(true);
      }

      return passwordChangeRequired;
    } catch (error) {
      console.warn('Exception checking password change requirement:', error);
      return false;
    }
  }, [isConfigured]);

  // Optimized admin status check with caching - no retries, fast timeout
  const checkAdminStatus = useCallback(async (userId: string): Promise<boolean> => {
    if (!userId || !isConfigured) return false;

    // Check cache first - if fresh, return immediately
    const now = Date.now();
    if (adminCheckCacheRef.current &&
        adminCheckCacheRef.current.userId === userId &&
        (now - adminCheckCacheRef.current.timestamp) < ADMIN_CACHE_DURATION) {
      if (import.meta.env.DEV) {
        console.log('📦 Using cached admin status:', adminCheckCacheRef.current.status);
      }
      return adminCheckCacheRef.current.status;
    }

    try {
      // Single attempt with 1-second timeout
      const queryPromise = supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .maybeSingle();

      const timeoutPromise = new Promise<any>((_, reject) =>
        setTimeout(() => reject(new Error('Admin status check timeout')), 1000)
      );

      const { data: profileData, error: profileError } = await Promise.race([
        queryPromise,
        timeoutPromise
      ]) as any;

      if (profileError) {
        if (import.meta.env.DEV) {
          console.warn('Admin status check error:', profileError.message);
        }
        // Use localStorage fallback on error
        const cachedStatus = localStorage.getItem('career_lens_admin_status');
        const cachedEmail = localStorage.getItem('career_lens_admin_email');
        if (cachedEmail === userId && cachedStatus !== null) {
          return cachedStatus === 'true';
        }
        return false;
      }

      const adminStatus = profileData?.is_admin === true;

      // Update cache
      adminCheckCacheRef.current = {
        status: adminStatus,
        timestamp: now,
        userId: userId
      };

      // Store in localStorage as backup
      localStorage.setItem('career_lens_admin_status', adminStatus ? 'true' : 'false');
      localStorage.setItem('career_lens_admin_email', userId);
      localStorage.setItem('career_lens_admin_status_timestamp', now.toString());

      return adminStatus;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.warn('Admin status check exception:', error.message);
      }

      // Use localStorage fallback
      const cachedStatus = localStorage.getItem('career_lens_admin_status');
      const cachedEmail = localStorage.getItem('career_lens_admin_email');
      if (cachedEmail === userId && cachedStatus !== null) {
        return cachedStatus === 'true';
      }

      return false;
    }
  }, [isConfigured, ADMIN_CACHE_DURATION]);

  // Public method to refresh admin status (clears cache and re-checks)
  const refreshAdminStatus = useCallback(async (): Promise<boolean> => {
    if (!user || !isConfigured) return false;

    try {
      // Clear cache to force fresh check
      adminCheckCacheRef.current = null;
      localStorage.removeItem('career_lens_admin_status_timestamp');

      const isUserAdmin = await checkAdminStatus(user.id);
      setIsAdmin(isUserAdmin);
      return isUserAdmin;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error refreshing admin status:', error);
      }
      return false;
    }
  }, [user, isConfigured, checkAdminStatus]);

  // Development-only logging helper
  const devLog = (...args: any[]) => {
    if (import.meta.env.DEV) {
      console.log(...args);
    }
  };
  // Profile repair function
  const repairUserProfile = async (email: string, password: string): Promise<boolean> => {
    if (!isConfigured) {
      toast.error('Application is not properly configured. Check environment variables.');
      return false;
    }

    try {
      console.log('AuthContext: Attempting profile repair for:', email);
      
      // Try to repair through the edge function
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        // Not logged in, try to authenticate first
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (error) {
          console.error('Authentication failed:', error);
          throw new Error('Authentication failed, cannot repair profile');
        }
        
        if (!data.user) {
          throw new Error('No user data received after authentication');
        }
        
        // Use the edge function to repair
        const { data: repairData, error: repairError } = await supabase.functions.invoke(
          'repair-user-profile',
          {
            body: { userId: data.user.id, email, fullName: data.user.user_metadata?.full_name || '' }
          }
        );
        
        if (repairError) {
          console.error('Edge function repair failed:', repairError);
          
          // Fallback to direct method
          return await createUserProfile(
            data.user.id,
            data.user.user_metadata?.full_name || ''
          );
        }
        
        return repairData.success;
      } else {
        // Already logged in, try to repair
        const { data: repairData, error: repairError } = await supabase.functions.invoke(
          'repair-user-profile',
          {
            body: { 
              userId: userData.user.id, 
              email: userData.user.email || email,
              fullName: userData.user.user_metadata?.full_name || '' 
            }
          }
        );
        
        if (repairError) {
          console.error('Edge function repair failed:', repairError);
          
          // Fallback to direct method
          return await createUserProfile(
            userData.user.id,
            userData.user.user_metadata?.full_name || ''
          );
        }
        
        return repairData.success;
      }
    } catch (error: any) {
      console.error('Error repairing profile:', error);
      
      // Check for specific error types and provide better messages
      if (error.message.includes('The profiles table does not exist')) {
        toast.error('Database setup issue: The profiles table does not exist');
      } else if (error.message.includes('permission denied')) {
        toast.error('Permission issue: The application cannot access the profiles table');
      } else {
        toast.error(error.message || 'Unknown error repairing profile');
      }
      
      return false;
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      return;
    }

    let isMounted = true;
    let initTimeout: NodeJS.Timeout | null = null;
    let authSubscription: any;

    const initializeAuth = async () => {
      // Prevent re-entry
      if (isInitializingRef.current) {
        return;
      }

      isInitializingRef.current = true;
      devLog('AuthContext: Starting initialization');
      
      try {
        devLog('AuthContext: Starting auth initialization...');

        // If not configured, set offline mode
        if (!isConfigured) {
          devLog('AuthContext: Supabase not configured - running in offline mode');
          if (isMounted) {
            setLoading(false);
            setAuthInitialized(true);
          }
          return;
        }
        
        // Set a reduced timeout to prevent infinite loading
        initTimeout = setTimeout(() => {
          if (isMounted) {
            devLog('AuthContext: Auth initialization timeout reached - forcing completion');
            setLoading(false);
            setAuthInitialized(true);
          }
        }, 3000); // 3 second timeout for faster startup
        
        // Get current session with error handling
        let currentSession = null;
        let sessionError = null;

        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          currentSession = session;
          sessionError = error;
        } catch (fetchError: any) {
          devLog('AuthContext: Exception during getSession():', fetchError);
          sessionError = fetchError;

          // If getSession fails, force completion to prevent infinite loading
          if (isMounted) {
            setLoading(false);
            setAuthInitialized(true);
            setUser(null);
            setSession(null);
            setIsAdmin(false);
          }
          return; // Exit early on getSession failure
        }

        // Clear timeout on successful response
        if (initTimeout) {
          clearTimeout(initTimeout);
          initTimeout = null;
        }

        if (sessionError && !sessionError.message?.includes('Failed to fetch')) {
          if (import.meta.env.DEV) {
            console.error('AuthContext: Error getting session:', sessionError);
          }
          if (isMounted) {
            setSession(null);
            setIsAdmin(false);
            setLoading(false);
            setAuthInitialized(true);
          }
          return;
        }
        
        if (currentSession && currentSession.user) {
          devLog('AuthContext: Valid session found for user:', currentSession.user.email);

          // Set session and user immediately - don't wait for admin check
          setSession(currentSession);
          setUser(currentSession.user);

          // Complete auth initialization IMMEDIATELY - don't block on admin check
          if (isMounted) {
            setLoading(false);
            setAuthInitialized(true);
          }

          // Check admin status in background (non-blocking)
          checkAdminStatus(currentSession.user.id).then(adminStatus => {
            if (isMounted) {
              setIsAdmin(adminStatus);
              devLog('Admin status set:', adminStatus);
            }
          }).catch(error => {
            devLog('Admin status check failed:', error);
            // Use cached fallback
            const cachedStatus = localStorage.getItem('career_lens_admin_status');
            const cachedEmail = localStorage.getItem('career_lens_admin_email');
            if (cachedEmail === currentSession.user.id && cachedStatus !== null) {
              setIsAdmin(cachedStatus === 'true');
            }
          });

          // Check if password change is required (non-blocking)
          checkPasswordChangeRequired(currentSession.user);
        } else {
          devLog('AuthContext: No active session found');
          setUser(null);
          setSession(null);
          setIsAdmin(false);

          localStorage.removeItem('career_lens_admin_status');
          localStorage.removeItem('career_lens_admin_email');
          localStorage.removeItem('career_lens_admin_status_timestamp');

          if (isMounted) {
            setLoading(false);
            setAuthInitialized(true);
          }
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('AuthContext: Error during auth initialization:', error);
        }
        if (isMounted) {
          setLoading(false);
          setAuthInitialized(true);
        }
      } finally {
        // Always reset the initialization flag
        isInitializingRef.current = false;
      }
    };

    // Initialize immediately - no artificial delay
    initializeAuth();
    
    // Listen for auth changes
    if (isConfigured) {
      const { data } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (!isMounted) return;

        devLog('AuthContext: Auth state change:', event);

        // Handle token refresh
        if (event === 'TOKEN_REFRESHED' && newSession) {
          setSession(newSession);
          setUser(newSession.user);
          return;
        }

        // Handle session expiry
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          localStorage.removeItem('career_lens_admin_status');
          localStorage.removeItem('career_lens_admin_email');
          localStorage.removeItem('career_lens_admin_status_timestamp');
          return;
        }

        if (event === 'SIGNED_IN' && newSession) {
          setSession(newSession);
          setUser(newSession.user);

          // Check admin status in background (non-blocking)
          checkAdminStatus(newSession.user.id).then(adminStatus => {
            if (isMounted) {
              setIsAdmin(adminStatus);
            }
          }).catch(error => {
            devLog('Admin status check failed:', error);
            setIsAdmin(false);
          });

          checkPasswordChangeRequired(newSession.user);
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          devLog('AuthContext: Duplicate SIGNED_OUT/USER_DELETED event');

          const now = Date.now();
          const timeSinceLastAttempt = now - lastSignOutAttemptRef.current;

          if (timeSinceLastAttempt < 3000) {
            return; // Ignore duplicate
          }

          lastSignOutAttemptRef.current = now;

          if (signOutDebounceRef.current) {
            clearTimeout(signOutDebounceRef.current);
          }

          signOutDebounceRef.current = setTimeout(async () => {
            try {
              const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

              if (!sessionError && currentSession) {
                // Valid session still exists
                setSession(currentSession);
                setUser(currentSession.user);
                return;
              }

              // Confirmed sign-out
              setSession(null);
              setUser(null);
              setIsAdmin(false);
              localStorage.removeItem('career_lens_admin_status');
              localStorage.removeItem('career_lens_admin_email');
              localStorage.removeItem('career_lens_admin_status_timestamp');
            } catch (error) {
              devLog('Error verifying sign-out:', error);
              setSession(null);
              setUser(null);
              setIsAdmin(false);
              localStorage.removeItem('career_lens_admin_status');
              localStorage.removeItem('career_lens_admin_email');
              localStorage.removeItem('career_lens_admin_status_timestamp');
            }
          }, 500);
        } else if (event === 'TOKEN_REFRESHED') {
          if (newSession) {
            setSession(newSession);
            setUser(newSession.user);
          }
        }
      });

      authSubscription = data.subscription;
    }

    return () => {
      isMounted = false;
      isInitializingRef.current = false;

      if (initTimeout) clearTimeout(initTimeout);
      if (signOutDebounceRef.current) clearTimeout(signOutDebounceRef.current);
      if (authSubscription) authSubscription.unsubscribe();
    };
  }, [isConfigured]);

  // Helper function to create profile directly
  const createProfileDirectly = async (userId: string, name: string): Promise<boolean> => {
    try {
      const result = await createUserProfile(userId, name);
      return result.success;
    } catch (error) {
      console.error('Error creating profile directly:', error);
      return false;
    }
  };

  // Helper function to ensure admin profile exists
  const ensureAdminProfile = async (userId: string, email: string): Promise<void> => {
    try {
      devLog('AuthContext: Ensuring admin profile exists for:', email);

      // First try using the safe profile creation function
      try {
        const { data: safeCreateResult, error: safeCreateError } = await supabase
          .rpc('create_user_profile_safe', {
            user_id: userId,
            full_name: 'Admin User'
          });

        if (!safeCreateError && safeCreateResult) {
          return;
        }
      } catch (safeError) {
        devLog('Safe creation function failed, trying direct approach:', safeError);
      }

      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id, is_admin')
        .eq('id', userId)
        .maybeSingle();

      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }

      if (!existingProfile) {
        // Create admin profile
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            full_name: 'Admin User',
            is_admin: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }
      } else if (!existingProfile.is_admin) {
        // Update existing profile to admin
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            is_admin: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (updateError) {
          throw updateError;
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('AuthContext: Error ensuring admin profile:', error);
      }
      throw error;
    }
  };

  // Simplified signUp function - No trigger dependency
  const signUp = async (email: string, password: string, name: string) => {
    if (!isConfigured) {
      throw new Error('Application is not properly configured');
    }

    setLoading(true);

    try {
      devLog('Starting signup for:', email);

      // STEP 1: Create auth user
      // This now ALWAYS succeeds because there's no trigger to block it
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
        },
      });

      if (error) {
        devLog('Auth signup error:', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user data returned from signup');
      }

      devLog('Auth user created successfully:', data.user.id);

      // STEP 2: Create profile explicitly using RPC
      // This happens immediately after auth user creation
      devLog('Creating profile for user:', data.user.id);

      const { data: profileResult, error: profileError } = await supabase
        .rpc('ensure_user_profile_exists', {
          user_id: data.user.id,
          user_email: email,
          full_name: name
        });

      if (profileError) {
        devLog('Profile creation error:', profileError);
        // Profile creation failed, but auth user exists
        // User can still sign in and profile will be created on login
        toast.success('Account created! Please sign in to complete setup.');
        return;
      }

      if (profileResult?.success) {
        devLog('Profile created successfully');
        toast.success('Account created successfully! Welcome to FutureLens.');

        localStorage.setItem('new_user_needs_subscription', 'true');
      } else {
        // Unexpected result format
        devLog('Unexpected profile creation result:', profileResult);
        toast.success('Account created! Please sign in to continue.');
      }

    } catch (error: any) {
      devLog('Signup error:', error);

      // Parse and provide user-friendly error messages
      let errorMessage = error.message || 'Failed to create account';

      // Handle specific error cases
      if (errorMessage.includes('User already registered') ||
          errorMessage.includes('email_exists') ||
          errorMessage.includes('user_already_exists')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (errorMessage.includes('weak_password')) {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (errorMessage.includes('invalid_email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (errorMessage.includes('Password should be at least')) {
        // Keep Supabase password requirement messages as-is
      }

      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Sign in function
  const signIn = async (email: string, password: string) => {
    if (!isConfigured) {
      throw new Error('Application is not properly configured');
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // Auto-repair: Check if profile exists, create if missing
      if (data.user) {
        try {
          devLog('Checking if profile exists for user:', data.user.id);

          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', data.user.id)
            .maybeSingle();

          if (!profile && !profileError) {
            devLog('Profile missing, creating now...');
            toast.info('Setting up your profile...');

            // Use the ensure_user_profile_exists function
            const { data: ensureResult, error: ensureError } = await supabase.rpc('ensure_user_profile_exists', {
              user_id: data.user.id,
              user_email: data.user.email || null,
              full_name: data.user.user_metadata?.full_name || data.user.email || 'User'
            });

            if (!ensureError && ensureResult?.success) {
              devLog('Profile created successfully on login');
              toast.success('Welcome to FutureLens!');
            } else {
              devLog('Profile creation failed, trying fallback');

              // Fallback to create_user_profile_safe
              const { data: rpcResult, error: rpcError } = await supabase.rpc('create_user_profile_safe', {
                user_id: data.user.id,
                full_name: data.user.user_metadata?.full_name || data.user.email || 'User'
              });

              if (!rpcError && rpcResult?.success) {
                devLog('Profile created via fallback method');
                toast.success('Welcome to FutureLens!');
              } else {
                devLog('All profile creation methods failed');
                toast.warning('Logged in, but profile setup is incomplete. Some features may be limited.');
              }
            }
          } else {
            devLog('Profile exists');
            toast.success('Welcome to FutureLens!');
          }
        } catch (profileCheckError) {
          devLog('Exception during profile check/creation:', profileCheckError);
          toast.success('Welcome to FutureLens!');
        }
      } else {
        toast.success('Welcome to FutureLens!');
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Sign in error:', error);
      }
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      console.log('Sign out initiated');
      
      // Clear all localStorage items related to our app
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase') || key.includes('future_lens'))) {
          console.log(`Removing localStorage item: ${key}`);
          localStorage.removeItem(key);
        }
      }
      
      // Clear session storage as well
      sessionStorage.clear();
      
      // Clear our app state immediately
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      
      if (isConfigured) {
        try {
          console.log('Calling Supabase signOut');
          const { error } = await supabase.auth.signOut();
          
          if (error) {
            console.error('Sign out error:', error);
            toast.error('Error signing out');
          }
        } catch (signOutError) {
          console.error('Exception during signOut:', signOutError);
          toast.error('Error during sign out');
        }
      } else {
        // Just clear state and storage
        console.log('Supabase not configured, clearing state only');
      }
      
      // Navigate after a short delay to ensure state is cleared
      console.log('Navigating to home page');
      setTimeout(() => {
        navigate('/');
      }, 100);
      
    } catch (error: any) {
      console.error('Sign out error:', error);
      
      // Force clear
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      
      // Clear all storage
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase') || key.includes('future_lens'))) {
          localStorage.removeItem(key);
        }
      }
      sessionStorage.clear();
      
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Function to check database connection and profile access
  const checkDatabaseConnection = useCallback(async (): Promise<boolean> => {
    if (!isConfigured) return false;
    
    try {
      console.log('Checking database connection and profile access');
      
      // Try edge function first
      try {
        const { data, error } = await supabase.functions.invoke(
          'check-database-connectivity',
          { body: {} }
        );
        
        if (error) {
          console.error('Error calling database check edge function:', error);
          throw error;
        }
        
        if (data.results.profiles_table.exists) {
          console.log('Profiles table exists and is accessible');
          return true;
        } else {
          console.warn('Profiles table access issue detected via edge function');
          return false;
        }
      } catch (edgeFunctionError) {
        console.warn('Edge function check failed:', edgeFunctionError);
        // Fall back to direct check
      }
      
      // Direct check
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
        
      if (error) {
        if (error.message.includes('relation "profiles" does not exist')) {
          console.error('Profiles table does not exist or is not accessible');
          return false;
        }
        
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking database connection:', error);
      return false;
    }
  }, [isConfigured]);

  // Context value
  const contextValue = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    repairUserProfile,
    loading,
    isAdmin,
    refreshAdminStatus,
    isConfigured,
    authInitialized,
    checkDatabaseConnection,
    needsPasswordChange,
    checkPasswordChangeRequired,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      
      {/* Password Change Prompt for Invited Users */}
      <PasswordChangePrompt
        isOpen={showPasswordPrompt}
        onClose={() => setShowPasswordPrompt(false)}
        onPasswordChanged={() => {
          setNeedsPasswordChange(false);
          setShowPasswordPrompt(false);
          toast.success('Welcome to FutureLens! Your account is now ready to use.');
        }}
      />
    </AuthContext.Provider>
  );
};